import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PersonCareData, CaregiverCareData, TermsAndConditionsData } from '../../types';

interface FormDataState {
  personCare: PersonCareData;
  caregiverCare: CaregiverCareData;
  termsAndConditions: TermsAndConditionsData;
}

const initialState: FormDataState = {
  personCare: {
    lastName: '',
    name: '',
    birthday: '',
    address: '',
    houseNumber: '',
    zipCode: '',
    city: '',
    phoneNumber: '',
    insurance: '',
    attachmentForInsuranceCard: '',
    certificate: '',
    legalRepresentation: '',
  },
  caregiverCare: {
    lastName: '',
    name: '',
    address: '',
    houseNumber: '',
    zipCode: '',
    city: '',
    phoneNumber: '',
    document: '',
  },
  termsAndConditions: {
    iHere: '',
    ConsentToTheData: '',
    iAgree: '',
    iHave: '',
    signature: '',
  },
};

const formDataSlice = createSlice({
  name: 'formData',
  initialState,
  reducers: {
    // Person Care
    setPersonCareField: (
      state,
      action: PayloadAction<{ field: keyof PersonCareData; value: string }>
    ) => {
      state.personCare[action.payload.field] = action.payload.value;
    },
    setPersonCareData: (state, action: PayloadAction<Partial<PersonCareData>>) => {
      state.personCare = { ...state.personCare, ...action.payload };
    },

    // Caregiver Care
    setCaregiverField: (
      state,
      action: PayloadAction<{ field: keyof CaregiverCareData; value: string }>
    ) => {
      state.caregiverCare[action.payload.field] = action.payload.value;
    },
    setCaregiverData: (state, action: PayloadAction<Partial<CaregiverCareData>>) => {
      state.caregiverCare = { ...state.caregiverCare, ...action.payload };
    },

    // Terms and Conditions
    setTermsField: (
      state,
      action: PayloadAction<{ field: keyof TermsAndConditionsData; value: string }>
    ) => {
      state.termsAndConditions[action.payload.field] = action.payload.value;
    },
    setTermsData: (state, action: PayloadAction<Partial<TermsAndConditionsData>>) => {
      state.termsAndConditions = { ...state.termsAndConditions, ...action.payload };
    },

    // Bulk load from API
    loadFormData: (state, action: PayloadAction<Partial<FormDataState>>) => {
      if (action.payload.personCare) {
        state.personCare = { ...state.personCare, ...action.payload.personCare };
      }
      if (action.payload.caregiverCare) {
        state.caregiverCare = { ...state.caregiverCare, ...action.payload.caregiverCare };
      }
      if (action.payload.termsAndConditions) {
        state.termsAndConditions = {
          ...state.termsAndConditions,
          ...action.payload.termsAndConditions,
        };
      }
    },

    // Reset
    resetFormData: () => initialState,

    // Reset Caregiver Data (used when switching from Nein to Ja)
    resetCaregiverData: (state) => {
      state.caregiverCare = {
        lastName: '',
        name: '',
        address: '',
        houseNumber: '',
        zipCode: '',
        city: '',
        phoneNumber: '',
        document: '',
      };
    },

    // Reset Terms Data (used when user needs to re-sign due to new data)
    resetTermsData: (state) => {
      state.termsAndConditions = initialState.termsAndConditions;
    },
  },
});

export const {
  setPersonCareField,
  setPersonCareData,
  setCaregiverField,
  setCaregiverData,
  setTermsField,
  setTermsData,
  loadFormData,
  resetFormData,
  resetCaregiverData,
  resetTermsData,
} = formDataSlice.actions;

export default formDataSlice.reducer;
